import { AnySourceStream, Stream, Subscription } from '../stream'
import { Pass } from './pass'

export class WaitUntil<V, E, C> extends Pass<V, E, C> {
    private sub: Subscription<V, E, C>
    private waiting = true

    private queuedValues: V[] = []

    constructor(
        private streamToWait: AnySourceStream<V, E, C>,
        source?: AnySourceStream<V, E, C>,
        private dontQueueValues?: boolean
    ) {
        super(source)

        const error = (err: E) => this._error(err)

        const start = () => {
            this.waiting = false

            this.queuedValues.forEach((v) => {
                this._value(v)
            })
            this.queuedValues = []

            streamToWait.unsubscribe(this.sub)
        }

        this.sub = { value: start, error, complete: start }
    }

    connect() {
        this.queuedValues = []

        super.connect()
        this.streamToWait.subscribe(this.sub)
    }

    disconnect() {
        this.queuedValues = []

        super.disconnect()
        this.streamToWait.unsubscribe(this.sub)
    }

    value(value: V) {
        if (this.waiting) {
            if (this.dontQueueValues) {
                return
            }
            this.queuedValues.push(value)
            return
        }

        this._value(value)
    }
}

declare module '../stream' {
    interface Stream<SV = any, SE = any, SC = any, DV = SV, DE = SE, DC = SC> {
        waitUntil(streamToWait: Stream): MirrorStream<DV, DE, DC>
    }
}

Stream.prototype.waitUntil = function <V, E, C>(
    this: AnySourceStream<V, E, C>,
    streamToWait: Stream,
    dontQueueValues?: boolean
) {
    return new WaitUntil(streamToWait, this, dontQueueValues)
}
